import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import type { ReviewAccountPlatform } from '@sparkapp/shared-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateReviewAccount } from '@/hooks/useReviewAccounts';
import { ApiError } from '@/services/apiClient';

const EMPTY = { email: '', platform: 'ios' as ReviewAccountPlatform, nickname: '', enabled: false, windowEnd: '' };

export default function AddReviewAccountForm() {
  const createAccount = useCreateReviewAccount();
  const [form, setForm] = useState(EMPTY);
  const [emailError, setEmailError] = useState<string | null>(null);

  const set = <K extends keyof typeof EMPTY>(key: K, value: (typeof EMPTY)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    const email = form.email.trim();
    if (!email) {
      setEmailError('Email is required.');
      return;
    }

    createAccount.mutate(
      {
        email,
        platform: form.platform,
        ...(form.nickname.trim() && { nickname: form.nickname.trim() }),
        enabled: form.enabled,
        ...(form.enabled && form.windowEnd && { expiresAt: new Date(form.windowEnd).toISOString() }),
      },
      {
        onSuccess: (account) => {
          toast.success(`Added ${account.email} (${account.status})`);
          setForm(EMPTY);
        },
        onError: (err) => {
          // 409 (email taken) and 400 (invalid email) belong under the email field
          if (err instanceof ApiError && (err.status === 409 || err.status === 400)) setEmailError(err.message);
          else toast.error(err.message);
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add reviewer</CardTitle>
      </CardHeader>
      <CardContent>
        <form data-testid="add-review-account-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="review-account-email">
                Email <span className="text-red-400">*</span>
              </Label>
              <Input
                id="review-account-email"
                data-testid="add-review-account-email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="ios-reviewer@test.playmoments.app"
              />
              {emailError && <p className="text-sm text-red-400">{emailError}</p>}
              <p className="text-sm text-pine-ink-soft">
                Use a new address only, e.g. <code>ios-reviewer@test.playmoments.app</code>. Reviewers sign
                in by typing this email; no inbox is needed.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="review-account-platform">
                Platform <span className="text-red-400">*</span>
              </Label>
              <select
                id="review-account-platform"
                data-testid="add-review-account-platform"
                value={form.platform}
                onChange={(e) => set('platform', e.target.value as ReviewAccountPlatform)}
                className="h-10 w-full px-3 rounded-md border border-pine-line bg-white text-pine-ink text-sm"
              >
                <option value="ios">iOS</option>
                <option value="android">Android</option>
              </select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="review-account-nickname">Nickname</Label>
              <Input
                id="review-account-nickname"
                value={form.nickname}
                onChange={(e) => set('nickname', e.target.value)}
                placeholder="App Reviewer"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="review-account-enabled">Sign-in</Label>
              <label className="flex h-10 items-center gap-2 text-sm text-pine-ink">
                <input
                  id="review-account-enabled"
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => set('enabled', e.target.checked)}
                  className="h-4 w-4"
                />
                Open sign-in now
              </label>
            </div>
            {form.enabled && (
              <div className="space-y-2">
                <Label htmlFor="review-account-window-end">Window ends</Label>
                <Input
                  id="review-account-window-end"
                  type="datetime-local"
                  value={form.windowEnd}
                  onChange={(e) => set('windowEnd', e.target.value)}
                />
                <p className="text-sm text-pine-ink-soft">Empty = open-ended</p>
              </div>
            )}
          </div>

          <button
            type="submit"
            data-testid="add-review-account-submit"
            disabled={createAccount.isPending}
            className="admin-button-primary"
          >
            {createAccount.isPending ? 'Adding...' : 'Add reviewer'}
          </button>
        </form>
      </CardContent>
    </Card>
  );
}
